// EDV Next 0.10.9 - React module-navigation components (JSX only).
// PURE helpers live in modules.ts so they can be tested from bare Node.
import React, { useMemo, useEffect, useCallback } from 'react';
import type { SessionMe } from '@edv-next/contracts';
import {
  MODULE_KEYS,
  MODULE_LABEL,
  STORAGE_KEY,
  entitledModules,
  validateStoredModule,
  firstEntitled,
  readStoredModule,
  writeStoredModule,
  type ModuleKey,
} from './module-core.js';

export { MODULE_KEYS, MODULE_LABEL, STORAGE_KEY, entitledModules, validateStoredModule, firstEntitled, readStoredModule, writeStoredModule };
export type { ModuleKey };

export interface ModuleNavProps {
  entitled: readonly ModuleKey[];
  active: ModuleKey | null;
  onSelect(k: ModuleKey): void;
}

export function ModuleNav({ entitled, active, onSelect }: ModuleNavProps) {
  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!entitled.length) return;
    const i = active ? entitled.indexOf(active) : 0;
    if (i < 0) return;
    let next = i;
    if (e.key === 'ArrowRight') next = (i + 1) % entitled.length;
    else if (e.key === 'ArrowLeft')  next = (i - 1 + entitled.length) % entitled.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End')  next = entitled.length - 1;
    else return;
    e.preventDefault();
    onSelect(entitled[next]!);
  }, [entitled, active, onSelect]);

  if (entitled.length === 0) return null;

  return (
    <nav aria-label="EDV Next modules">
      <div
        role="tablist"
        aria-label="EDV Next modules"
        style={{ display: 'flex', gap: 8, borderBottom: '1px solid #ddd', padding: '4px 0' }}
      >
        {entitled.map((k) => {
          const selected = k === active;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              id={`module-tab-${k}`}
              aria-controls={`module-panel-${k}`}
              aria-selected={selected}
              aria-current={selected ? 'page' : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => onSelect(k)}
              onKeyDown={onKeyDown}
              data-testid={`module-tab-${k}`}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderBottom: selected ? '2px solid #005b9f' : '1px solid #ccc',
                background: selected ? '#f0f7ff' : '#fff',
                fontWeight: selected ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {MODULE_LABEL[k]}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/** Selector hook: derives active module + entitled list; persists selection. */
export function useActiveModule(me: SessionMe | null, orgId: string | null): {
  active: ModuleKey | null;
  entitled: ModuleKey[];
  setActive: (k: ModuleKey) => void;
} {
  const entitled = useMemo(() => entitledModules(me, orgId), [me, orgId]);
  const [active, setActiveState] = React.useState<ModuleKey | null>(() => {
    const stored = validateStoredModule(readStoredModule(), entitled);
    return stored ?? firstEntitled(entitled);
  });
  useEffect(() => {
    if (active && (entitled as readonly string[]).includes(active)) return;
    const stored = validateStoredModule(readStoredModule(), entitled);
    const next = stored ?? firstEntitled(entitled);
    setActiveState(next);
    if (next !== null && next !== active) writeStoredModule(next);
  }, [entitled, active]);
  const setActive = useCallback((k: ModuleKey) => {
    setActiveState(k);
    writeStoredModule(k);
  }, []);
  return { active, entitled, setActive };
}
